import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API, { fmt } from '@/lib/api';
import { Warning, CheckCircle, ArrowsClockwise, ShieldCheck } from '@phosphor-icons/react';

/**
 * Consistentie-controle paneel voor een document met bron_id.
 * Toont verschillen met brondocument en biedt actieknoppen:
 *   - Navigeer naar bron
 *   - Update vanuit bron (sync)
 *   - Markeer als gecontroleerd (pre-verzending)
 */
export default function ControlePanel({ docId, klanten = [], onChange }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => {
    if (!docId) return;
    API.get(`/documenten/${docId}/controle`).then(r => setData(r.data)).catch(() => setData(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [docId]);

  if (!data) return null;
  if (!data.bron) return null; // geen brondocument = geen controle

  const heeftVerschillen = (data.verschillen || []).length > 0;
  const gecontroleerd = data.gecontroleerd;

  const klantNaam = (kid) => {
    const k = klanten.find(x => x.id === kid);
    if (!k) return kid || '—';
    return k.bedrijf || `${k.voorn || ''} ${k.achtern || ''}`.trim() || kid;
  };

  const syncFromBron = async () => {
    if (!window.confirm(
      `Dit document (${docId}) overschrijven met de huidige gegevens van ${data.bron.id}?\n\n` +
      '• Klant, lijnen, totalen en BTW-bedragen worden vervangen.\n' +
      '• Alleen uitvoeren als dit document nog niet definitief verzonden is.'
    )) return;
    setBusy(true); setMsg('');
    try {
      await API.post(`/documenten/${data.bron.id}/sync-kinderen`, { include_sent: true, kind_ids: [docId] });
      setMsg('✓ Dit document is bijgewerkt met de gegevens van het brondocument.');
      load();
      onChange?.();
    } catch (e) { setMsg('Fout: ' + (e.response?.data?.detail || e.message)); }
    setBusy(false);
  };

  const markeer = async () => {
    setBusy(true); setMsg('');
    try {
      await API.post(`/documenten/${docId}/markeer-gecontroleerd`);
      setMsg('✓ Dit document staat op "Gecontroleerd".');
      load();
    } catch (e) { setMsg('Fout: ' + (e.response?.data?.detail || e.message)); }
    setBusy(false);
  };

  const kleurRand = heeftVerschillen ? '#DC2626' : (gecontroleerd ? '#16A34A' : '#F59E0B');
  const bg = heeftVerschillen ? '#FEF2F2' : (gecontroleerd ? '#F0FDF4' : '#FFFBEB');

  return (
    <div className="bg-white overflow-hidden" style={{ border: '1px solid #E5E5E5', borderLeft: `4px solid ${kleurRand}` }} data-testid="controle-panel">
      <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between" style={{ background: bg }}>
        <span className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1" style={{ color: kleurRand }}>
          {heeftVerschillen ? <Warning size={12} weight="fill" /> : (gecontroleerd ? <ShieldCheck size={12} weight="fill" /> : <Warning size={12} />)}
          Verzend-controle
        </span>
        <button
          type="button"
          onClick={() => nav(`/documenten/${data.bron.id}/bewerk`)}
          className="text-[10px] text-[#002FA7] hover:underline font-bold font-mono"
          data-testid="controle-bron-link"
          title="Naar brondocument"
        >
          ← {data.bron.id}
        </button>
      </div>

      <div className="p-3 space-y-2">
        <div className="text-[11px] text-gray-700">
          Opgemaakt op basis van <b>{data.bron.type}</b> <span className="font-mono text-[#002FA7]">{data.bron.id}</span>.
          Controleer dat alle gegevens kloppen <b>vóór verzending</b>.
        </div>

        {heeftVerschillen ? (
          <>
            <div className="p-2 rounded border border-red-200 bg-red-50">
              <div className="text-[10px] font-bold uppercase text-red-700 mb-1">Verschillen met brondocument</div>
              <table className="w-full text-[10px]">
                <thead className="text-gray-500">
                  <tr>
                    <th className="text-left py-0.5">Veld</th>
                    <th className="text-left py-0.5">Dit document</th>
                    <th className="text-left py-0.5">Brondocument</th>
                  </tr>
                </thead>
                <tbody>
                  {data.verschillen.map((v, i) => (
                    <tr key={i} className="border-t border-red-100">
                      <td className="py-1 font-mono">{v.veld}</td>
                      <td className="py-1 text-red-800">
                        {v.veld === 'klant_id' ? klantNaam(v.doc) : (v.veld === 'totaal' ? fmt(v.doc || 0) : v.doc)}
                      </td>
                      <td className="py-1 text-green-800 font-bold">
                        {v.veld === 'klant_id' ? klantNaam(v.bron) : (v.veld === 'totaal' ? fmt(v.bron || 0) : v.bron)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-primary w-full justify-center"
              onClick={syncFromBron}
              disabled={busy}
              data-testid="controle-sync"
            >
              <ArrowsClockwise size={12} /> Bijwerken vanuit brondocument
            </button>
          </>
        ) : gecontroleerd ? (
          <div className="p-2 rounded border border-green-200 bg-green-50 text-[11px] text-green-800 flex items-center gap-2">
            <CheckCircle size={16} weight="fill" />
            <div>
              <div className="font-bold">Gecontroleerd ✓</div>
              <div className="text-[10px] text-green-700">Alle gegevens komen overeen met het brondocument.</div>
            </div>
          </div>
        ) : (
          <>
            <div className="p-2 rounded border border-green-200 bg-green-50 text-[11px] text-green-800">
              ✓ Geen verschillen met brondocument gevonden.
            </div>
            <button
              type="button"
              className="btn btn-sm btn-ghost w-full justify-center"
              onClick={markeer}
              disabled={busy}
              data-testid="controle-ok"
            >
              <ShieldCheck size={12} /> Markeer als gecontroleerd
            </button>
          </>
        )}

        {msg && <div className="text-[10px] text-gray-600 pt-1 border-t border-gray-100">{msg}</div>}
      </div>
    </div>
  );
}
